import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, MessageSquare, Phone, Mail, CheckCircle2, RefreshCw, Clock, AlertTriangle, Crown, Sparkles, BellRing } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import { StatusPill, EmptyState, TableRowsSkeleton } from '../ds';
import { RequestThread } from './RequestThread';

const API_URL = getApiUrl();
const CHANNEL_ICON = { chat: MessageSquare, whatsapp: MessageSquare, call: Phone, email: Mail, form: MessageSquare };

// SLA chip for time-boxed enquiries (V26 — Elite Till-Delivery 24h callback).
const SlaBadge = ({ dueAt, status }) => {
  if (!dueAt || (status !== 'open')) return null;
  const diffMs = new Date(dueAt).getTime() - Date.now();
  const overdue = diffMs <= 0;
  const hrs = Math.round(Math.abs(diffMs) / 3.6e6);
  const label = overdue ? `SLA overdue ${hrs}h` : `${hrs}h to call`;
  return (
    <span data-testid="request-sla-badge"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
      {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{label}
    </span>
  );
};

// V17 §7.4 — requests raised inside the APP. `kind` splits the desk's two queues:
// "support" (help/tech/other) and "sales" (counsellor + Till-Delivery enquiries).
export const LeadRequestsPanel = ({ onOpenLead, kind = 'sales' }) => {
  const [state, setState] = useState({ loading: true, items: [], openCount: 0 });
  const [status, setStatus] = useState('open');
  const [openThread, setOpenThread] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = { category: kind, ...(status === 'all' ? {} : { status }) };
      const { data } = await axios.get(`${API_URL}/api/leads/requests`, { params, withCredentials: true });
      setState({ loading: false, items: data.requests || [], openCount: data.open_count || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load requests');
      setState((s) => ({ ...s, loading: false }));
    }
  }, [status, kind]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, next) => {
    try {
      await axios.put(`${API_URL}/api/leads/requests/${id}`, { status: next }, { withCredentials: true });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="card" data-testid="lead-requests-panel">
      <div className="card-header flex flex-wrap items-center justify-between gap-3 py-4">
        <div>
          <h2 className="font-display text-base md:text-lg font-semibold">
            {kind === 'support' ? 'Support tickets from the APP' : 'Sales queries from the APP'}
          </h2>
          <p className="text-xs text-muted-foreground">
            {kind === 'support'
              ? <>Help, tech and account issues raised by learners. <b>{state.openCount}</b> open.</>
              : <>Learners who asked to talk to sales. <b>{state.openCount}</b> open.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-1" role="tablist">
            {['open', 'contacted', 'converted', 'closed', 'all'].map((k) => (
              <button key={k} type="button" role="tab" aria-selected={status === k} onClick={() => setStatus(k)} data-testid={`requests-filter-${k}`}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold capitalize ${status === k ? 'bg-card text-primary shadow-xs' : 'text-slate-600'}`}>{k}</button>
            ))}
          </div>
          <button type="button" onClick={load} className="btn-ghost p-1.5" aria-label="Refresh" data-testid="requests-refresh"><RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      {state.loading && !state.items.length ? <TableRowsSkeleton rows={4} cols={5} /> : state.items.length === 0 ? (
        <div className="p-6"><EmptyState icon={MessageSquare} title="No requests" hint="When a learner taps “Talk to sales” in the APP, it lands here with their tier and score." /></div>
      ) : (
        <table className="w-full">
          <thead className="table-header">
            <tr><th className="px-4 py-2.5 text-left">Lead</th><th className="px-4 py-2.5 text-left">Request</th><th className="px-4 py-2.5 text-left">Tier / Score</th><th className="px-4 py-2.5 text-left">Received</th><th className="px-4 py-2.5 text-right">Action</th></tr>
          </thead>
          <tbody>
            {state.items.map((r) => {
              const Icon = CHANNEL_ICON[r.channel] || MessageSquare;
              return (
                <React.Fragment key={r.request_id}>
                <tr className="table-row" data-testid={`request-row-${r.request_id}`}>
                  <td className="table-cell">
                    <button type="button" className="font-medium text-left hover:text-primary" onClick={() => r.lead_id && onOpenLead?.(r.lead_id)} data-testid={`request-open-lead-${r.request_id}`}>
                      {r.lead_name || r.email || r.phone || '—'}
                    </button>
                    <p className="text-[11px] text-muted-foreground">{r.email || r.phone || ''}</p>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <Icon className="w-3.5 h-3.5 text-slate-400" /><span className="capitalize">{r.channel || 'app'}</span>
                      {r.td_kind === 'elite' || r.is_elite_till_delivery ? (
                        <span data-testid={`request-elite-td-${r.request_id}`} className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                          <Crown className="w-3 h-3" /> Elite Till-Delivery
                        </span>
                      ) : (r.td_kind === 'soulai' || r.is_soulai_till_delivery) ? (
                        <span data-testid={`request-soulai-td-${r.request_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                          <Sparkles className="w-3 h-3" /> SoulAI Till-Delivery
                        </span>
                      ) : (r.enquiry_kind === 'postnatal_upsell' || r.plan === 'postnatal_programme') ? (
                        <span data-testid={`request-postnatal-programme-${r.request_id}`} className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                          <Sparkles className="w-3 h-3" /> Postnatal programme
                        </span>
                      ) : (r.enquiry_kind === 'elite_renewal' || r.plan === 'elite_renewal') ? (
                        <span data-testid={`request-elite-renewal-${r.request_id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                          <Crown className="w-3 h-3" /> Elite renewal
                        </span>
                      ) : (r.category && <StatusPill status="pending" label={r.category} className="!py-0" />)}
                      {r.priority === 'high' && !r.td_kind && !r.is_elite_till_delivery && !r.is_soulai_till_delivery && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">High priority</span>}
                      <SlaBadge dueAt={r.sla_due_at} status={r.status} />
                      {(r.escalation || (r.repeat_count > 0)) && (
                        <span data-testid={`request-nudge-${r.request_id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                          <BellRing className="w-3 h-3" /> Nudge ×{r.repeat_count || 1}
                        </span>
                      )}
                      {r.edd && <span className="text-[11px] text-slate-500">EDD {String(r.edd).slice(0, 10)}</span>}
                      {r.context?.months ? <span className="text-[11px] text-slate-500">{r.context.months} mo</span> : null}
                      {r.context?.discount_pct_shown ? <span className="text-[11px] text-slate-500">shown {r.context.discount_pct_shown}% off</span> : null}
                    </div>
                    {r.message_excerpt && <p className="mt-1 text-xs text-slate-600 italic truncate max-w-sm">“{r.message_excerpt}”</p>}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1.5">
                      <StatusPill status={r.tier || 'free'} label={(r.tier || 'free').replace('_', ' ')} />
                      {r.learner_tier && r.learner_tier !== r.tier && (
                        <span data-testid={`request-learner-tier-${r.request_id}`} className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700 capitalize">
                          LMS {String(r.learner_tier).replace('_', ' ')}
                        </span>
                      )}
                      {r.is_hot && <Flame className="w-4 h-4 text-primary" aria-label="HOT" />}
                      {typeof r.score === 'number' && <span className="font-mono text-xs text-slate-500">{r.score}</span>}
                    </div>
                  </td>
                  <td className="table-cell text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</td>
                  <td className="table-cell text-right">
                    <StatusPill status={r.status} className="mr-2" />
                    {r.status === 'open' && <button type="button" className="btn-secondary text-xs py-1" onClick={() => update(r.request_id, 'contacted')} data-testid={`request-contacted-${r.request_id}`}>Contacted</button>}
                    {r.status === 'contacted' && <button type="button" className="btn-primary text-xs py-1 inline-flex items-center gap-1" onClick={() => update(r.request_id, 'converted')} data-testid={`request-converted-${r.request_id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Converted</button>}
                    {(r.status === 'open' || r.status === 'contacted') && <button type="button" className="btn-ghost text-xs py-1 ml-1" onClick={() => update(r.request_id, 'closed')} data-testid={`request-closed-${r.request_id}`}>Close</button>}
                    <button type="button" className="btn-ghost text-xs py-1 ml-1 inline-flex items-center gap-1"
                      onClick={() => setOpenThread(openThread === r.request_id ? null : r.request_id)}
                      data-testid={`request-reply-toggle-${r.request_id}`}>
                      <MessageSquare className="w-3.5 h-3.5" />
                      Reply{r.messages?.length ? ` (${r.messages.length})` : ''}
                    </button>
                  </td>
                </tr>
                {openThread === r.request_id && (
                  <tr><td colSpan={5} className="p-0">
                    <RequestThread request={r} onSent={load} />
                  </td></tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
